import { Provider } from "jotai";
import { useContext, useEffect, useMemo, useState } from "react";

import { initVal } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import { QuoteDetailsForm } from "~/features/quote/components/quote-details/index.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "~/features/quote/context/quote-details-context.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";

import { usePostSimulatedQuote } from "../hooks/use-post-simulated-quote.ts";
import { CarrierSelectSimulate } from "./components/carrier-select-simulate.tsx";

const SimulateQuoteContent = ({
	forceExternal,
	onPendingChange,
}: {
	forceExternal?: boolean;
	onPendingChange: (pending: boolean) => void;
}) => {
	const [step, setStep] = useState<1 | 2>(1);
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { createSimulatedQuote, isPending } = usePostSimulatedQuote();

	useEffect(() => {
		onPendingChange(isPending);
	}, [isPending, onPendingChange]);

	return step === 1 ? (
		<QuoteDetailsForm
			simulateMode
			forceExternal={forceExternal}
			isSubmitting={isPending}
			onStepChange={() => undefined}
			onSubmit={async () => {
				await createSimulatedQuote({
					quoteDetails: quoteState,
					selectedAddressBookItems,
				});
				setStep(2);
				return { success: true };
			}}
			submitButtonText={"Get Simulated Rates"}
		/>
	) : (
		<CarrierSelectSimulate />
	);
};

export const SimulateQuoteModal = ({
	handleClose,
	showSimulateModal,
	forceExternal,
	customer,
}: {
	handleClose: () => void;
	showSimulateModal: boolean;
	forceExternal?: boolean;
	customer?: CustomerDetails;
}) => {
	const [isPending, setIsPending] = useState(false);
	useAutoSpinner(isPending);

	const initialQuoteState = useMemo(
		() =>
			customer
				? {
						...initVal,
						customer: {
							...initVal.customer,
							...customer,
						},
						customerDetails: {
							...initVal.customerDetails,
							...customer,
						},
					}
				: undefined,
		[customer],
	);

	if (!showSimulateModal) return null;
	return (
		<TraxModal onClose={handleClose} title={"Simulate Quote"} width={"98%"}>
			<Provider>
				<QuoteDetailsProvider initialQuoteState={initialQuoteState}>
					<SimulateQuoteContent
						forceExternal={forceExternal}
						onPendingChange={setIsPending}
					/>
				</QuoteDetailsProvider>
			</Provider>
		</TraxModal>
	);
};
